import { useEffect, useState } from "react";
import { BodyDiagram } from "../components/BodyDiagram";
import { RecentAtSitePanel } from "../components/RecentAtSitePanel";
import { BtnPrimary, Card } from "../components/ui";
import { isInjectableRoute, siteLabel } from "../lib/injections";
import { supabase } from "../supabaseClient";
import { S, css } from "../theme";

function TrackToday({ protocol, todayLog, setTodayLog, user }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calMonth, setCalMonth] = useState(new Date());
  const [history, setHistory] = useState({}); // { "Mon May 19 2026": { protocolId: true } }
  const [loadingHist, setLoadingHist] = useState(true);
  const [selectedLog, setSelectedLog] = useState({});
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 900);
  const [injLogs, setInjLogs] = useState([]);       // injection_sites rows, newest-first
  const [todayLinks, setTodayLinks] = useState({}); // { protocolId: injection_site_id } for today
  const [pendingDose, setPendingDose] = useState(null); // dose awaiting a site pick
  const [pickSite, setPickSite] = useState(null);   // chosen site inside the picker
  const [hoverSite, setHoverSite] = useState(null); // hovered site → recent preview

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Load injection history so check-off can log a site and show rotation status
  useEffect(() => {
    async function load() {
      if (!user) return;
      const { data } = await supabase.from("injection_sites").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
      setInjLogs(data || []);
    }
    load();
  }, [user]);

  const isToday = selectedDate.toDateString() === new Date().toDateString();

  // Load all historical logs once
  useEffect(() => {
    async function load() {
      if (!user) { setLoadingHist(false); return; }
      setLoadingHist(true);
      const { data } = await supabase.from("daily_logs").select("log_date, protocol_id, checked, injection_site_id").eq("user_id", user.id);
      const map = {};
      const todayKey = new Date().toDateString();
      const links = {};
      (data || []).forEach(l => {
        (map[l.log_date] = map[l.log_date] || {})[l.protocol_id] = l.checked;
        if (l.log_date === todayKey && l.injection_site_id) links[l.protocol_id] = l.injection_site_id;
      });
      setHistory(map);
      setTodayLinks(links);
      setLoadingHist(false);
    }
    load();
  }, [user]);

  // Keep today's log in sync with the live todayLog prop
  useEffect(() => {
    setHistory(prev => ({ ...prev, [new Date().toDateString()]: todayLog }));
  }, [todayLog]);

  // When selected date changes, load its log into selectedLog
  useEffect(() => {
    const key = selectedDate.toDateString();
    setSelectedLog(history[key] || {});
  }, [selectedDate, history]);

  function toggle(id) {
    const p = protocol.find(x => x.id === id);
    const currentlyOn = isToday ? !!todayLog[id] : !!selectedLog[id];
    if (isToday && p && isInjectableRoute(p.route)) {
      // Turning ON → ask where they injected first
      if (!currentlyOn) {
        setPendingDose(p);
        setPickSite(null);
        setHoverSite(null);
        return;
      }
      // Turning OFF → also undo the injection logged with this dose
      removeInjectionFor(id);
    }
    doToggle(id);
  }

  // Delete today's injection linked to a dose (used when the dose is unchecked).
  async function removeInjectionFor(protocolId) {
    const injId = todayLinks[protocolId];
    if (!injId) return; // no site was logged for this dose
    if (user) await supabase.from("injection_sites").delete().eq("id", injId);
    setInjLogs(prev => prev.filter(l => l.id !== injId));
    setTodayLinks(prev => { const next = { ...prev }; delete next[protocolId]; return next; });
  }

  async function doToggle(id) {
    const dateKey = selectedDate.toDateString();
    if (isToday) {
      // use the live wrapper that writes to DB
      setTodayLog(prev => ({ ...prev, [id]: !prev[id] }));
      return;
    }
    // past/backfill date — write directly to DB
    const newVal = !selectedLog[id];
    setSelectedLog(prev => ({ ...prev, [id]: newVal }));
    setHistory(prev => ({ ...prev, [dateKey]: { ...(prev[dateKey] || {}), [id]: newVal } }));
    if (!user) return;
    if (newVal) {
      await supabase.from("daily_logs").upsert(
        { user_id: user.id, log_date: dateKey, protocol_id: id, checked: true },
        { onConflict: "user_id,log_date,protocol_id" }
      );
    } else {
      await supabase.from("daily_logs").delete().eq("user_id", user.id).eq("log_date", dateKey).eq("protocol_id", id);
    }
  }

  function closePicker() { setPendingDose(null); setPickSite(null); setHoverSite(null); }

  // Confirm the picker: optionally log the injection site, then mark the dose taken.
  // siteId === null means the user skipped the site.
  async function confirmInjection(siteId) {
    const p = pendingDose;
    if (!p) return;
    let injId = null;
    if (siteId && user) {
      const { data } = await supabase.from("injection_sites").insert({
        user_id: user.id, site: siteId, peptide: p.name, injected_date: new Date().toDateString(),
      }).select().single();
      if (data) { injId = data.id; setInjLogs(prev => [data, ...prev]); }
    }
    setTodayLinks(prev => ({ ...prev, [p.id]: injId }));
    // Pass the injection id so it's stored on the daily_logs row (the link the
    // uncheck path reads back to remove the injection).
    setTodayLog(prev => ({ ...prev, [p.id]: true }), injId);
    closePicker();
  }

  const activeLog = isToday ? todayLog : selectedLog;
  const checked = protocol.filter(p => activeLog[p.id]).length;
  const pct = protocol.length ? Math.round((checked / protocol.length) * 100) : 0;

  // Build calendar grid for calMonth
  const year = calMonth.getFullYear();
  const month = calMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  function dayStatus(date) {
    if (!date) return null;
    if (date > today) return "future";
    const key = date.toDateString();
    const log = history[key];
    if (!log || !protocol.length) return "none";
    const done = protocol.filter(p => log[p.id]).length;
    if (done === 0) return "none";
    if (done >= protocol.length) return "full";
    return "partial";
  }

  // Compute current streak (consecutive days up to today with at least one log)
  function computeStreak() {
    let streak = 0;
    let d = new Date();
    for (let i = 0; i < 365; i++) {
      const key = d.toDateString();
      const log = history[key];
      const done = log ? protocol.filter(p => log[p.id]).length : 0;
      if (done > 0) { streak++; d.setDate(d.getDate() - 1); }
      else break;
    }
    return streak;
  }
  const streak = computeStreak();

  const monthName = calMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div style={{ padding: 40 }} className="tmp-page-pad">
      <h2 style={{ ...css.fontDisplay, fontSize: "1.8rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", marginBottom: 8 }}>Track</h2>
      <p style={{ color: S.muted, fontSize: "0.85rem", marginBottom: 24 }}>Log your doses and build your streak. Tap any past day to backfill.</p>

      {protocol.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 60 }}>
          <div style={{ fontSize: "3rem", marginBottom: 16 }}>📋</div>
          <div style={{ ...css.fontDisplay, fontSize: "1.3rem", fontWeight: 700, marginBottom: 8 }}>No protocol set up</div>
          <p style={{ color: S.muted, fontSize: "0.88rem" }}>Add peptides to your protocol first, then track them here.</p>
        </Card>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) minmax(0, 1.2fr)", gap: isMobile ? 18 : 24, alignItems: "start" }} className="tmp-track-grid">
          {/* LEFT: Calendar + streak */}
          <Card style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <button onClick={() => setCalMonth(new Date(year, month - 1, 1))} style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 10, width: 34, height: 34, cursor: "pointer", fontSize: "1rem", color: S.ink2, flexShrink: 0 }}>‹</button>
              <span style={{ ...css.fontDisplay, fontWeight: 700, fontSize: "1.05rem", color: S.ink, whiteSpace: "nowrap" }}>{monthName}</span>
              <button onClick={() => setCalMonth(new Date(year, month + 1, 1))} style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 10, width: 34, height: 34, cursor: "pointer", fontSize: "1rem", color: S.ink2, flexShrink: 0 }}>›</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 8 }}>
              {["S","M","T","W","T","F","S"].map((d, i) => (
                <div key={i} style={{ textAlign: "center", fontSize: "0.7rem", fontWeight: 700, color: S.muted, padding: "4px 0" }}>{d}</div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
              {cells.map((date, i) => {
                if (!date) return <div key={i} />;
                const status = dayStatus(date);
                const isSel = date.toDateString() === selectedDate.toDateString();
                const isTod = date.toDateString() === today.toDateString();
                let bg = "transparent", color = S.ink2, border = "1px solid transparent";
                if (status === "full") { bg = S.accent; color = "#fff"; }
                else if (status === "partial") { bg = S.accent + "33"; color = S.accent; }
                else if (status === "future") { color = S.border; }
                if (isSel) border = `2px solid ${S.accent2 || S.ink}`;
                else if (isTod) border = `1.5px dashed ${S.muted}`;
                return (
                  <button key={i} disabled={status === "future"} onClick={() => setSelectedDate(date)}
                    style={{ height: 0, paddingBottom: "100%", position: "relative", borderRadius: 10, background: bg, color, border, cursor: status === "future" ? "default" : "pointer", fontSize: "0.82rem", fontWeight: isTod || isSel ? 700 : 500, fontFamily: "'Inter', sans-serif", transition: "all 0.15s", overflow: "hidden" }}>
                    <span style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>{date.getDate()}</span>
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 16, marginTop: 20, flexWrap: "wrap" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.72rem", color: S.muted }}><span style={{ width: 12, height: 12, borderRadius: 4, background: S.accent }} /> All logged</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.72rem", color: S.muted }}><span style={{ width: 12, height: 12, borderRadius: 4, background: S.accent + "33" }} /> Partial</span>
            </div>

            {/* Streak */}
            <div style={{ marginTop: 20, padding: "18px 20px", background: "linear-gradient(135deg, #3d9b72, #16271f)", borderRadius: 14, color: "#fff", display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ fontSize: "2rem", flexShrink: 0 }}>🔥</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...css.fontDisplay, fontWeight: 900, fontSize: "1.5rem", lineHeight: 1.1, whiteSpace: "nowrap" }}>{streak} day{streak === 1 ? "" : "s"}</div>
                <div style={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.7)", marginTop: 2, whiteSpace: "nowrap" }}>current streak</div>
              </div>
            </div>
          </Card>

          {/* RIGHT: Selected day's doses */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div style={{ ...css.fontDisplay, fontWeight: 700, fontSize: "1.1rem", color: S.ink }}>
                  {isToday ? "Today" : selectedDate.toLocaleDateString("en-US", { weekday: "long" })}
                </div>
                <div style={{ fontSize: "0.8rem", color: S.muted }}>{selectedDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
              </div>
              <div style={{ ...css.fontDisplay, fontWeight: 900, fontSize: "1.4rem", color: pct === 100 ? S.accent : S.ink }}>{pct}%</div>
            </div>

            <div style={{ height: 8, background: S.border, borderRadius: 100, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? S.accent : (S.accent2 || S.accent), borderRadius: 100, transition: "width 0.4s ease" }} />
            </div>
            <p style={{ fontSize: "0.78rem", color: S.muted, marginBottom: 16 }}>{checked} of {protocol.length} doses logged{isToday ? " today" : ""}</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {protocol.map(p => (
                <div key={p.id} onClick={() => toggle(p.id)} style={{ display: "flex", alignItems: "center", gap: 16, padding: "18px 20px", background: activeLog[p.id] ? S.accentLight : S.white, border: `1.5px solid ${activeLog[p.id] ? S.accent + "66" : S.border}`, borderRadius: 16, cursor: "pointer", transition: "all 0.2s" }}>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: "0.95rem", color: S.ink }}>{p.name}</div>
                    <div style={{ fontSize: "0.78rem", color: S.muted, marginTop: 2 }}>{p.dose} · {p.route} · {p.frequency}</div>
                    {activeLog[p.id] && isToday && (() => {
                      const inj = injLogs.find(l => l.peptide === p.name && new Date(l.created_at).toDateString() === new Date().toDateString());
                      return (
                        <div style={{ fontSize: "0.72rem", color: S.accent, marginTop: 4, fontWeight: 600 }}>✓ Taken{inj ? ` · ${siteLabel(inj.site)}` : ""}</div>
                      );
                    })()}
                  </div>
                  <div style={{ width: 36, height: 36, borderRadius: 10, border: `2px solid ${activeLog[p.id] ? S.accent : S.border}`, background: activeLog[p.id] ? S.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "1rem", flexShrink: 0 }}>
                    {activeLog[p.id] ? "✓" : ""}
                  </div>
                </div>
              ))}
            </div>

            {pct === 100 && (
              <Card style={{ marginTop: 20, background: S.accentLight, border: `1px solid ${S.accent}44`, textAlign: "center", padding: 28 }}>
                <div style={{ fontSize: "2rem", marginBottom: 10, opacity: 0.9 }}>🌿</div>
                <div style={{ ...css.fontDisplay, fontWeight: 600, fontStyle: "italic", fontSize: "1.15rem", color: S.accent }}>{isToday ? "Nice — that's today." : "Backfilled."}</div>
                <p style={{ color: S.muted, fontSize: "0.84rem", marginTop: 6, fontStyle: "italic", fontFamily: "'Fraunces', serif" }}>{isToday ? "One more day on the page. That's all this is." : "Saved. Back on track."}</p>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Injection site picker — opens when an injectable dose is checked off */}
      {pendingDose && (
        <div onClick={closePicker} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}>
          <Card onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 820, maxHeight: "92vh", overflowY: "auto", padding: 32 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
              <div style={{ ...css.fontDisplay, fontWeight: 800, fontSize: "1.3rem", color: S.ink }}>Where did you inject?</div>
              <button onClick={closePicker} style={{ background: "none", border: "none", color: S.muted, fontSize: "1.4rem", cursor: "pointer", lineHeight: 1, padding: 0 }}>✕</button>
            </div>
            <p style={{ fontSize: "0.86rem", color: S.muted, margin: "0 0 20px" }}>
              Logging <strong style={{ color: S.ink2 }}>{pendingDose.name}</strong>. Tap a site to pick it — hover any site to see its recent injections.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1fr", gap: 24, alignItems: "start" }}>
              <Card style={{ padding: 18 }}>
                <div style={{ display: "flex", gap: 14, justifyContent: "center" }}>
                  <BodyDiagram view="front" logs={injLogs} selectedSite={pickSite} onSelectSite={setPickSite} hoverSite={hoverSite} onHoverSite={setHoverSite} maxWidth={300} />
                  <BodyDiagram view="back" logs={injLogs} selectedSite={pickSite} onSelectSite={setPickSite} hoverSite={hoverSite} onHoverSite={setHoverSite} maxWidth={300} />
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center", marginTop: 12 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.66rem", color: S.muted }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: S.accent }} /> Rested</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.66rem", color: S.muted }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: "#c9a227" }} /> 3–6d</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.66rem", color: S.muted }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: S.accent2 }} /> Recent</span>
                </div>
              </Card>

              <div>
                {(hoverSite || pickSite)
                  ? <RecentAtSitePanel logs={injLogs} siteId={hoverSite || pickSite} />
                  : <p style={{ fontSize: "0.84rem", color: S.muted, lineHeight: 1.7, margin: "4px 0 0" }}>Hover a site to preview its last few injections, or tap to select where this dose went.</p>
                }
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
              <BtnPrimary onClick={() => confirmInjection(pickSite)} disabled={!pickSite} style={{ flex: 1, minWidth: 180 }}>
                {pickSite ? `Log dose · ${siteLabel(pickSite)}` : "Pick a site"}
              </BtnPrimary>
              <button onClick={() => confirmInjection(null)} style={{ flex: "0 0 auto", padding: "12px 18px", background: "none", border: `1.5px solid ${S.border}`, borderRadius: 100, color: S.muted, cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
                No site / skip
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── INJECTION SITE OVERVIEW (read-only) ─────────────────────
// Logging now happens on the Track page when an injectable dose is checked
// off. This page is a glanceable rotation map + history. Hover (or tap, on
// mobile) a site to see its most recent injections.

export { TrackToday };
